/**
 * What the Connectors & MCP page's data becomes on the map (docs/claude/home2-map.md "Inputs to buildWorld"):
 *
 *  - channels IN (left): the connected connectors that carry conversations with people;
 *  - connectors (under the ring): every OTHER connected connector, plus the connected MCP servers — only
 *    what the account actually wired up (the row ends in a "+ connect" node);
 *  - outreach (right): the fixed product list of ways Symphony reaches people, each flagged connected or not.
 *
 * Pure; mirrored on the phones.
 */
import { OUTREACH_CHANNELS, type MapInputs } from './map-layout';
import { listedOnTools } from '../outreach-alfred';

/**
 * The CHANNELS — the Connectors & MCP genome's left column, the surfaces Symphony hears you through
 * (`CHANNEL_SLUGS` on the connectors screen): WhatsApp and the apps & web. Every CONNECTOR (Gmail,
 * a calendar, LinkedIn, Wix, …) is a TOOL and sits under the ring, exactly as the genome files it on the
 * right. Gmail is not "a channel in" — it is something the agent reads and writes through.
 */
export const CHANNELS_IN: { slug: string; label: string }[] = [
  { slug: 'whatsapp', label: 'WhatsApp' },
  { slug: 'email-in', label: 'Email' },
  { slug: 'sms-in', label: 'SMS' },
  { slug: 'web', label: 'Web' },
];

/** What else the account hears through — the phone app (when installed) and every AI coder / MCP client
 *  (Claude Code, Gemini Enterprise, ChatGPT…) the user connected to Symphony. */
export interface ChannelExtras {
  /** `true` when the phone app has been used on this account; `false`/`null` draws no node. */
  phoneApp?: boolean | null;
  /** One entry per connected AI-coder client, already grouped by name. */
  coders?: { id: string; label: string }[];
  /** This client draws Alfred on Connectors › Outreach, so Alfred is no tool node (`listedOnTools`). */
  alfredOnOutreach?: boolean;
}
/** The outreach channels that are BRANDS — drawn with their logo from the Composio logo service (the same
 *  system the Tools tab uses), like a connector. Email maps to `gmail` (the logo it ships). SMS and phone
 *  are generic and keep a glyph; iMessage has no real Composio logo (the service returns a placeholder), so
 *  it is drawn from a bundled brand mark at the call site instead. The values are the logo service's slugs. */
const BRANDED_OUTREACH: Record<string, string> = { whatsapp: 'whatsapp', instagram: 'instagram', x: 'twitter', email: 'gmail' };

/** An agent connection is a CHANNEL only when it is an AI-coder / assistant client the user talks through
 *  (Claude Code, Claude, ChatGPT, Gemini, Cursor, Codex, Copilot…). Anything else registered as an MCP
 *  client — a smart-home hub, an integration — is a tool, not a way to talk to Symphony. */
const CODER_PLATFORMS = /claude|anthropic|chatgpt|openai|gemini|cursor|codex|copilot|windsurf|cline|aider/i;
export function isCoderClient(c: { agentName?: string; agentPlatform?: string; agentUrl?: string }): boolean {
  return CODER_PLATFORMS.test(`${c.agentPlatform ?? ''} ${c.agentName ?? ''} ${c.agentUrl ?? ''}`);
}
export function outreachLogoUrl(slug: string): string | null {
  const key = BRANDED_OUTREACH[slug];
  return key ? `https://logos.composio.dev/api/${key}` : null;
}

/** Wix is always a tool: every Symphony account is a Wix account (the genome draws it connected). */
export const ALWAYS_TOOLS: { slug: string; label: string }[] = [{ slug: 'wix', label: 'Wix' }];

export interface ConnectorLike {
  toolkit: string;
  name?: string;
  connected: boolean;
  logo?: string;
  /** `native` for a calendar row (Google / Microsoft / iCloud), which has no Composio toolkit. */
  provider?: string;
  calendarProvider?: string;
  /** The server's `connectors-hidden-from-list` flag: off the Tools tab, so not a tool node here either. */
  hiddenFromConnectorsList?: boolean;
  /** The provider refused the stored authorization: `connected` is bookkeeping, nothing can be sent. */
  needsReconnect?: boolean;
}

/** A native calendar row's slug and logo: the Composio toolkit that carries the same brand's logo (the
 *  connectors genome draws Google Calendar from `googlecalendar` too); iCloud has none and keeps a glyph. */
export function calendarSlug(c: ConnectorLike): { slug: string; logoUrl?: string } | null {
  if (c.provider !== 'native') return null;
  switch ((c.calendarProvider ?? '').toUpperCase()) {
    case 'GOOGLE':
      return { slug: 'googlecalendar', logoUrl: 'https://logos.composio.dev/api/googlecalendar' };
    case 'MICROSOFT':
    case 'OUTLOOK':
      return { slug: 'outlook', logoUrl: 'https://logos.composio.dev/api/outlook' };
    case 'ICLOUD':
    case 'APPLE':
      return { slug: 'applecalendar' };
    default:
      return null;
  }
}

export interface McpLike {
  id: string;
  name?: string;
  url?: string;
  status: string;
  connectorSlug?: string;
}

/** Hosts whose MCP server IS a known product — the honest name for the node and its card. */
const MCP_HOST_NAMES: Record<string, string> = {
  'nabu.casa': 'Home Assistant',
  'home-assistant.io': 'Home Assistant',
};

/**
 * What to call an MCP server on the map. A server that only has a URL for a name (the common case — the
 * connect flow stores the address) is named by its host: a known product by its product name, otherwise
 * the registrable domain, title-cased ("nabu.casa" → Home Assistant, "tools.acme.io" → "Acme").
 */
export function mcpDisplayName(m: Pick<McpLike, 'name' | 'url'>): string {
  const raw = (m.name ?? '').trim();
  const looksLikeUrl = /^(?:https?:\/\/)?[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:[/:?#]|$)/i.test(raw) && !raw.includes(' ');
  if (raw && !looksLikeUrl) return raw;
  const source = raw || m.url || '';
  const host = source
    .replace(/^[a-z]+:\/\//i, '')
    .split(/[/:?#]/)[0]
    .toLowerCase();
  if (!host) return 'MCP';
  const parts = host.split('.').filter(Boolean);
  const registrable = parts.slice(-2).join('.');
  if (MCP_HOST_NAMES[registrable]) return MCP_HOST_NAMES[registrable];
  const label = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Which outreach channel each connector slug stands behind, for the connected flag. */
const OUTREACH_BY_CONNECTOR: Record<string, string> = {
  gmail: 'email',
  outlook: 'email',
  whatsapp: 'whatsapp',
  sms: 'sms',
  instagram: 'instagram',
  facebook: 'messenger',
  x: 'x',
  twitter: 'x',
};

/** The channels Symphony sends on ITSELF, with no connector: they are connected for every account. */
export const SYMPHONY_SENDS = ['email', 'whatsapp', 'sms'] as const;

/** The channels Symphony sends on through the user's OWN connected account: live once that connector is
 *  connected, and offering to connect it until then (never "coming soon"). Instagram DM is the
 *  `INSTAGRAM_CONNECTOR` outreach channel — reply-only, on a thread the other person opened (Meta's rule). */
export const CONNECTOR_SENDS: Record<string, string> = { instagram: 'instagram' };

/** What an Outreach row offers: `live` (Symphony sends on it now), `connect` (a connector channel whose
 *  connector is not connected yet — the row offers Connect), `reconnect` (connected, but the provider
 *  refused the stored authorization, so nothing can go out until the owner reconnects), or `comingSoon`
 *  (a vote). A `needsReconnect` row is never `live`: a Connected badge over a channel that cannot send is
 *  exactly the screen this state exists to avoid. */
export type OutreachRowState = 'live' | 'connect' | 'reconnect' | 'comingSoon';
export function outreachRowState(slug: string, connectors: ConnectorLike[]): OutreachRowState {
  if ((SYMPHONY_SENDS as readonly string[]).includes(slug)) return 'live';
  const toolkit = CONNECTOR_SENDS[slug];
  if (!toolkit) return 'comingSoon';
  const rows = connectors.filter((c) => c.connected && c.toolkit === toolkit);
  if (rows.some((c) => !c.needsReconnect)) return 'live';
  return rows.length > 0 ? 'reconnect' : 'connect';
}

/** The fixed outreach list with each channel's connected flag. Email, WhatsApp and SMS are always on:
 *  Symphony sends them itself (the agent writes as "{agent} on {channel}"); Instagram DM is on once the
 *  Instagram connector is (`CONNECTOR_SENDS`); the rest are coming. */
export function outreachChannels(connectors: ConnectorLike[]): MapInputs['outreach'] {
  const wired = new Set<string>(SYMPHONY_SENDS);
  for (const c of connectors) {
    // A row Instagram (or any provider) refused cannot send, so its channel is not drawn connected.
    if (!c.connected || c.needsReconnect) continue;
    const channel = OUTREACH_BY_CONNECTOR[c.toolkit];
    if (channel) wired.add(channel);
  }
  return OUTREACH_CHANNELS.map((ch) => ({ slug: ch.slug, label: ch.label, connected: wired.has(ch.slug) }));
}

export function partitionConnectors(
  connectors: ConnectorLike[],
  mcp: McpLike[] = [],
  extras: ChannelExtras = {},
): Pick<MapInputs, 'channelsIn' | 'connectors' | 'outreach'> {
  // One node per toolkit: a multi-account connector (two Gmail accounts) is one Gmail on the map.
  const seen = new Set<string>(ALWAYS_TOOLS.map((t) => t.slug));
  const slugOf = (c: ConnectorLike) => calendarSlug(c)?.slug ?? c.toolkit;
  const connected = connectors.filter(
    (c) => c.connected && listedOnTools(c, extras.alfredOnOutreach === true) && slugOf(c) && !seen.has(slugOf(c)) && seen.add(slugOf(c)),
  );
  const label = (c: ConnectorLike) => c.name ?? c.toolkit;
  const channelsIn = [
    ...CHANNELS_IN.map((c) => ({ ...c })),
    ...(extras.phoneApp ? [{ slug: 'phone-app', label: 'Phone app' }] : []),
    ...(extras.coders ?? []).map((c) => ({ slug: `coder:${c.id}`, label: c.label })),
  ];
  const tools = [
    ...ALWAYS_TOOLS.map((t) => ({ ...t })),
    ...connected.map((c) => {
      const logo = c.logo ?? calendarSlug(c)?.logoUrl;
      return { slug: slugOf(c), label: label(c), ...(logo ? { logoUrl: logo } : {}) };
    }),
  ];
  // An MCP server connected by slug is a ready-made connector already in the list above; the rest are raw servers.
  const servers = mcp.filter((m) => m.status === 'CONNECTED' && !m.connectorSlug).map((m) => ({ slug: `mcp:${m.id}`, label: mcpDisplayName(m) }));
  return { channelsIn, connectors: [...tools, ...servers], outreach: outreachChannels(connectors) };
}
